import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCog,
  faPaperPlane,
  faTimes,
  faSignOutAlt,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { Units } from "../../model/UnitsModel";
import PopUp from "../CondPopUp/CondPopUp";
import Settings from "../Settings/Settings";
import PayPage from "../PayPage/PayPage";
import "./MainPageStyle.css";

interface SelectedUnit {
  ID: number;
  Unit: string;
  checked: boolean;
}

interface MainPageViewState {
  keyword: string;
  street: string;
  streetNum: string;
  postalCode: string;
  place: string;
  selection: string;
  latitude: string;
  longitude: string;
  showPopUp: boolean;
  useAlarmMonitor: boolean;
  units: Units[];
  selectedUnits: SelectedUnit[];
  showSettingsModal: boolean;
  showPayModal: boolean;
  accountStatus: string;
  isExpired: boolean;
}

interface MainPageViewActions {
  setKeyword: (v: string) => void;
  setStreet: (v: string) => void;
  setStreetNum: (v: string) => void;
  setPostalCode: (v: string) => void;
  setPlace: (v: string) => void;
  setSelection: (v: string) => void;
  setLatitude: (v: string) => void;
  setLongitude: (v: string) => void;
  setUseAlarmMonitor: (v: boolean) => void;
  setSelectedUnits: React.Dispatch<React.SetStateAction<SelectedUnit[]>>;
  closeSettings: () => void;
  openPayModal: () => void;
  closePayModal: () => void;
  handleLogout: () => void;
}

interface Props {
  name: string;
  tag: string;
  token: string;
  loggedID: string;
  loggedState: number;
  testAlarms: number; // <-- Hier verfügbar
  agbStatus: number;
  dsgvoStatus: number;
  useCondStatus: number;
  state: MainPageViewState;
  actions: MainPageViewActions;
  onOpenSettings: () => void;
  onSend: () => void;
}

export default function MainPageView({
  name,
  tag,
  token,
  loggedID,
  loggedState,
  testAlarms, // <-- Hier verfügbar
  agbStatus,
  dsgvoStatus,
  useCondStatus,
  state,
  actions,
  onOpenSettings,
  onSend,
}: Props) {
  const formatDate = (dateString: string) => {
    if (!dateString) return "";
    const parts = dateString.split("-");
    if (parts.length === 3) {
      return `${parts[2].substring(0, 2)}.${parts[1]}.${parts[0]}`;
    }
    return dateString;
  };

  return (
    <div className="mp_pageWrapper">
      <div className="mp_mainContainer">
        {state.showPopUp && (
          <PopUp
            tag={tag}
            agbStatus={agbStatus}
            dsgvoStatus={dsgvoStatus}
            useCondStatus={useCondStatus}
          />
        )}

        {/* Header */}
        <div className="mp_header">
          <div>
            <h2>Neuer Einsatz für {name}</h2>
            <p className="mp_subtitle">Senden Sie Einsatzdaten in Echtzeit</p>
          </div>
          
          <div className="mp_headerActions">
            <button className="mp_settingsButton" onClick={onOpenSettings} title="Einstellungen">
              <FontAwesomeIcon icon={faCog} />
              <span>Einstellungen</span>
            </button>

            <button 
              className="mp_logoutButton" 
              onClick={() => actions.handleLogout()} 
              title="Abmelden"
            >
              <FontAwesomeIcon icon={faSignOutAlt} />
              <span>Abmelden</span>
            </button>
          </div>
        </div>

        {/* Dynamische Info / Warn-Banner */}
        {state.accountStatus !== "Payed" && (state.accountStatus === "Test" || state.isExpired) && (
          <div className="mp_warningBanner">
            <div className="mp_warningText">
              <FontAwesomeIcon icon={faExclamationTriangle} className="mp_warningIcon" />
              <span>
                {state.accountStatus === "Test" ? (
                  <>
                    <strong>Testphase aktiv:</strong> Es sind noch <strong>{testAlarms} Alarme</strong> übrig.
                  </>
                ) : (
                  <>
                    <strong>Achtung:</strong> Ihr Abonnement läuft am{" "}
                    <strong>{formatDate(state.accountStatus)}</strong> ab. Verlängern Sie rechtzeitig, um Ausfälle zu vermeiden.
                  </>
                )}
              </span>
            </div>
            <button className="mp_renewButton" onClick={actions.openPayModal}>
              Abo jetzt freischalten
            </button>
          </div>
        )}

        {/* Formular-Grid */}
        <div className="mp_formGrid">
          <div className="mp_card">
            <h3 className="mp_cardTitle">Einsatz-Informationen</h3>
            
            <div className="ja-login-inputContainer">
              <label className="ja-login-label">Stichwort eingeben:</label>
              <input
                value={state.keyword}
                placeholder="z. B. B2 - Gebäudebrand"
                onChange={(ev) => actions.setKeyword(ev.target.value)}
                className="ja-login-inputBox"
              />
            </div>

            {state.units.length > 0 && (
              <div className="ja-login-inputContainer">
                <label className="ja-login-label">Einheiten auswählen:</label>
                <div className="mp_tableWrapper">
                  <table className="mp_table">
                    <thead>
                      <tr>
                        <th className="mp_col-1">Auswahl</th>
                        <th className="mp_col-2">Einheit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {state.selectedUnits.map((einheit) => (
                        <tr className="mp_table-row" key={einheit.ID}>
                          <td className="mp_col-1">
                            <input
                              type="checkbox"
                              className="mp_checkbox"
                              checked={einheit.checked}
                              onChange={() =>
                                actions.setSelectedUnits((prev) =>
                                  prev.map((e) =>
                                    e.ID === einheit.ID
                                      ? { ...e, checked: !e.checked }
                                      : e
                                  )
                                )
                              }
                            />
                          </td>
                          <td className="mp_col-2">{einheit.Unit}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div className="mp_card">
            <h3 className="mp_cardTitle">Einsatzort</h3>

            <div className="ja-login-inputContainer">
              <label className="ja-login-label">Typ auswählen:</label>
              <select
                value={state.selection}
                onChange={(e) => actions.setSelection(e.target.value)}
                className="ja-login-inputBox mp_selectBox"
              >
                <option value="Adresse">Adresse</option>
                <option value="Koordinate">Koordinate</option>
              </select>
            </div>

            {state.selection === "Adresse" && (
              <>
                <div className="mp_inputRow">
                  <div className="ja-login-inputContainer mp_flex-3">
                    <label className="ja-login-label">Straße:</label>
                    <input
                      value={state.street}
                      placeholder="Musterstraße"
                      onChange={(ev) => actions.setStreet(ev.target.value)}
                      className="ja-login-inputBox"
                    />
                  </div>
                  <div className="ja-login-inputContainer mp_flex-1">
                    <label className="ja-login-label">Nr.:</label>
                    <input
                      value={state.streetNum}
                      placeholder="12a"
                      onChange={(ev) => actions.setStreetNum(ev.target.value)}
                      className="ja-login-inputBox"
                    />
                  </div>
                </div>

                <div className="mp_inputRow">
                  <div className="ja-login-inputContainer mp_flex-1">
                    <label className="ja-login-label">PLZ:</label>
                    <input
                      value={state.postalCode}
                      placeholder="12345"
                      onChange={(ev) => actions.setPostalCode(ev.target.value)}
                      className="ja-login-inputBox"
                    />
                  </div>
                  <div className="ja-login-inputContainer mp_flex-2">
                    <label className="ja-login-label">Ort:</label>
                    <input
                      value={state.place}
                      placeholder="Musterstadt"
                      onChange={(ev) => actions.setPlace(ev.target.value)}
                      className="ja-login-inputBox"
                    />
                  </div>
                </div>
              </>
            )}

            {state.selection === "Koordinate" && (
              <>
                <div className="ja-login-inputContainer">
                  <label className="ja-login-label">Längengrad:</label>
                  <input
                    value={state.longitude}
                    placeholder="z. B. 9.180769"
                    onChange={(ev) => actions.setLongitude(ev.target.value)}
                    className="ja-login-inputBox"
                  />
                </div>
                <div className="ja-login-inputContainer">
                  <label className="ja-login-label">Breitengrad:</label>
                  <input
                    value={state.latitude}
                    placeholder="z. B. 48.775845"
                    onChange={(ev) => actions.setLatitude(ev.target.value)}
                    className="ja-login-inputBox"
                  />
                </div>
              </>
            )}
          </div>
        </div>

        {/* Beta Box */}
        <div className="mp_betaBox">
          <div className="mp_betaBadge">BETA</div>
          <p>
            Durch Aktivieren wird neben der Alarmierung auf den Smartphones ein hinterlegter GroupAlarm
            Alarmmonitor aktiviert. Haben Sie Interesse an dieser Beta-Funktion,
            wenden Sie sich bitte an{" "}
            <a href="mailto:info@mbsoftwaresolutions.de">
              info@mbsoftwaresolutions.de
            </a>.
          </p>

          <label className="mp_checkboxLabel">
            <input
              type="checkbox"
              className="mp_checkbox"
              checked={state.useAlarmMonitor}
              onChange={() => actions.setUseAlarmMonitor(!state.useAlarmMonitor)}
            />
            <span>Alarmmonitor aktivieren</span>
          </label>
        </div>

        {/* Action Button */}
        <div className="mp_actionArea">
          <button
            type="button"
            className="ja-login-primaryButton mp_sendButton"
            onClick={onSend}
          >
            <FontAwesomeIcon icon={faPaperPlane} className="mp_sendIcon" />
            Einsatz Alarmieren
          </button>
        </div>
      </div>

      {/* Settings Modal */}
      {state.showSettingsModal && (
        <div className="ja-modal-overlay" onClick={actions.closeSettings}>
          <div className="ja-modal-card ja-modal-settingsCard" onClick={(e) => e.stopPropagation()}>
            <button className="ja-modal-close" onClick={actions.closeSettings}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <Settings
              tag={tag}
              token={token}
              testAlarms={testAlarms}
              loggedID={loggedID}
              loggedState={loggedState}
              onCloseModal={actions.closeSettings}
            />
          </div>
        </div>
      )}

      {/* Pay Modal */}
      {state.showPayModal && (
        <div className="ja-modal-overlay" onClick={actions.closePayModal}>
          <div className="ja-modal-card ja-modal-settingsCard" onClick={(e) => e.stopPropagation()}>
            <button className="ja-modal-close" onClick={actions.closePayModal}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
            <PayPage tag={tag} />
          </div>
        </div>
      )}
    </div>
  );
}